import { useState } from "react";
import {
  Mail,
  Github,
  Linkedin,
  MapPin,
  Send,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import useInView from "../lib/useInView";

const CONTACT_INFO = [
  {
    Icon: Mail,
    label: "Email",
    value: "khadimhussainj14@gmail.com",
    href: "mailto:khadimhussainj14@gmail.com",
  },
  {
    Icon: Github,
    label: "GitHub",
    value: "github.com/khadim-hussain-j",
    href: "https://github.com/khadim-hussain-j",
  },
  {
    Icon: Linkedin,
    label: "LinkedIn",
    value: "linkedin.com/in/khadim-hussainj",
    href: "https://linkedin.com/in/khadim-hussainj",
  },
  { Icon: MapPin, label: "Location", value: "Karachi, Pakistan", href: null },
];

const EMPTY = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [ref, visible] = useInView();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      // Replace YOUR_FORM_ID with your actual Formspree ID
      const res = await fetch("https://formspree.io/f/YOUR_FORM_ID", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus("success");
        setForm(EMPTY);
        setTimeout(() => setStatus("idle"), 5000);
      } else {
        setStatus("error");
        setTimeout(() => setStatus("idle"), 5000);
      }
    } catch {
      setStatus("error");
      setTimeout(() => setStatus("idle"), 5000);
    }
  };

  const inputCls =
    "w-full px-4 py-3 rounded-xl border border-[#1e293b] bg-[#111111] text-[#f1f5f9] text-sm placeholder-[#94a3b8]/50 focus:outline-none focus:border-indigo-500/60 focus:ring-1 focus:ring-indigo-500/30 transition-all duration-200";

  return (
    <section id="contact" className="section-padding bg-[#0a0a0a]">
      <div className="section-container" ref={ref}>
        <div
          className={`mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="section-label">// 06. contact</span>
          <h2 className="section-heading">Get In Touch</h2>
          <div className="section-divider" />
          <p className="text-[#94a3b8] max-w-xl">
            I'm looking for my first engineering role and I'm happy to chat —
            whether it's a job opportunity, a project idea, or just a question
            about something I've built.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact info */}
          <div
            className={`transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-8"
            }`}
          >
            <h3 className="text-lg font-semibold text-[#f1f5f9] mb-6">
              Contact Info
            </h3>

            <div className="space-y-4 mb-10">
              {CONTACT_INFO.map(({ Icon, label, value, href }) => (
                <div key={label} className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl border border-[#1e293b] bg-[#111111] flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-indigo-400" />
                  </div>
                  <div>
                    <p className="font-mono text-xs text-[#94a3b8] mb-0.5">
                      {label}
                    </p>
                    {href ? (
                      <a
                        href={href}
                        target={href.startsWith("http") ? "_blank" : undefined}
                        rel="noopener noreferrer"
                        className="text-[#f1f5f9] text-sm hover:text-indigo-400 transition-colors duration-200"
                      >
                        {value}
                      </a>
                    ) : (
                      <p className="text-[#f1f5f9] text-sm">{value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-xl border border-green-500/20 bg-green-500/5 p-5">
              <div className="flex items-center gap-2 mb-2">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                <span className="text-green-400 text-sm font-semibold">
                  Actively Job Hunting
                </span>
              </div>
              <p className="text-[#94a3b8] text-sm leading-relaxed">
                Looking for junior or entry-level roles in frontend or
                full-stack development. Open to full-time positions,
                internships, and contract work.
              </p>
            </div>
          </div>

          {/* Form */}
          <div
            className={`transition-all duration-700 delay-300 ${
              visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"
            }`}
          >
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block font-mono text-xs text-[#94a3b8] mb-2"
                    >
                      Name
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block font-mono text-xs text-[#94a3b8] mb-2"
                    >
                      Email
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className={inputCls}
                    />
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="subject"
                    className="block font-mono text-xs text-[#94a3b8] mb-2"
                  >
                    Subject
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    required
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="What's this about?"
                    className={inputCls}
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block font-mono text-xs text-[#94a3b8] mb-2"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell me about the project or opportunity..."
                    className={`${inputCls} resize-none`}
                  />
                </div>

                {status === "success" && (
                  <div className="flex items-center gap-2 p-3 rounded-xl border border-green-500/30 bg-green-500/10 text-green-400 text-sm">
                    <CheckCircle size={15} /> Message sent! I'll get back to you
                    soon.
                  </div>
                )}
                {status === "error" && (
                  <div className="flex items-center gap-2 p-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-400 text-sm">
                    <AlertCircle size={15} /> Something went wrong. Please email
                    me directly.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={status === "loading" || status === "success"}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-indigo-500/25"
                >
                  {status === "loading" ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={14} /> Send Message
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
